// iNaturalist serves the same photo at multiple sizes via URL suffix:
//   /square.jpg  (75×75, ~5 KB)
//   /small.jpg   (240×240, ~15 KB)
//   /medium.jpg  (500×500, ~40 KB)
//   /large.jpg   (1024×1024, ~120 KB)
//   /original.jpg
//
// Our backfill hands the client `medium` by default because that reads
// well in a detail hero — but row and tile thumbs are 62–88 px, so we're
// moving 5–10× the bytes we can actually display. This helper flips the
// URL to `square` for small thumbs (and to `small` for medium ones) so
// the on-wire bytes match the rendered pixels.
//
// Wikipedia / Wikimedia thumbnails come out of the REST API already
// pre-sized (~320 px), so we leave those alone rather than guessing at
// their URL scheme. GBIF and any other source falls through as-is.

const INAT_HOSTS = new Set([
  "static.inaturalist.org",
  "inaturalist-open-data.s3.amazonaws.com",
]);

const INAT_SIZE_RE = /\/(square|small|medium|large|original)\.(jpg|jpeg|png|gif|webp)/i;

export type ThumbSize = "square" | "small" | "medium" | "large" | "original";

/**
 * Return the same URL sized for the given display context. Unknown hosts
 * are returned unchanged so this is safe to call on every photo URL.
 */
export function sizedPhotoUrl(url: string, size: ThumbSize): string {
  if (!url) return url;
  let host: string;
  try {
    host = new URL(url).hostname;
  } catch {
    return url;
  }
  if (!INAT_HOSTS.has(host)) return url;
  // If the URL already has a size suffix, swap it; otherwise leave it
  // (some CDN-derived paths don't follow the pattern).
  if (!INAT_SIZE_RE.test(url)) return url;
  return url.replace(INAT_SIZE_RE, `/${size}.$2`);
}

/**
 * Convenience: pick the right iNat size for a rendered pixel width.
 * The buckets correspond roughly to iNat's own size tiers so the
 * on-wire image is a bit larger than the rendered box (allowing for
 * device pixel ratio) but not wastefully so.
 */
export function thumbForWidth(url: string, cssWidthPx: number): string {
  if (cssWidthPx <= 100) return sizedPhotoUrl(url, "square"); // 75px
  if (cssWidthPx <= 200) return sizedPhotoUrl(url, "small"); // 240px
  return url; // let the default medium/large through
}
